import React, { useEffect, useMemo, useRef, useState } from "react";
import { Rnd } from "react-rnd";
import styled from "styled-components";
import { colors, layout } from "./theme";
import { useWindowManager } from "./WindowManager";
import MenuBar from "./MenuBar";
import Dock from "./Dock";
import DesktopIcon from "./DesktopIcon";
import Window from "./Window";

const ICON_W = 92;
const ICON_BOX_H = 96;
const ICON_CELL_H = 104;
// A pointer that moves less than this between press and release counts as a
// click (open the app) rather than a drag (reposition the icon).
const CLICK_THRESHOLD = 5;

const Root = styled.div`
  position: fixed;
  inset: 0;
  overflow: hidden;
  background: linear-gradient(
    160deg,
    ${colors.wallpaperTop} 0%,
    ${colors.desktop} 45%,
    ${colors.wallpaperBottom} 100%
  );
`;

const Blob = styled.div<{ $color: string; $size: number }>`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  opacity: 0.35;
  filter: blur(60px);
  pointer-events: none;
`;

const IconsLayer = styled.div`
  position: absolute;
  top: ${layout.menuBarHeight}px;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 14px;
  z-index: 1;
  pointer-events: none;
`;

const WindowsLayer = styled.div`
  position: absolute;
  top: ${layout.menuBarHeight}px;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

const Marquee = styled.div`
  position: fixed;
  z-index: 2;
  border: 2px solid ${colors.ink};
  background: rgba(83, 218, 169, 0.2);
  border-radius: 6px;
  pointer-events: none;
`;

const Desktop = () => {
  const { registry, windows, getApp, openApp } = useWindowManager();
  const layerRef = useRef<HTMLDivElement>(null);
  const [area, setArea] = useState({ width: 1280, height: 720 });
  const [userPositions, setUserPositions] = useState<
    Record<string, { x: number; y: number }>
  >({});
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const dragStart = useRef({ x: 0, y: 0 });
  // Snapshot of every selected icon's position at the start of a group drag.
  const groupStart = useRef<Record<string, { x: number; y: number }> | null>(
    null
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [marquee, setMarquee] = useState<{
    x: number;
    y: number;
    w: number;
    h: number;
  } | null>(null);
  const marqueeStart = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const el = layerRef.current;
    if (!el) return;
    const update = () =>
      setArea({ width: el.clientWidth, height: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const desktopApps = useMemo(
    () => registry.filter((a) => a.desktop),
    [registry]
  );

  // Default desktop layout. Primary apps fill a top-left grid; the playful
  // "easter egg" apps (Music, Screensaver) are tucked into the bottom-right
  // corner so they read as extras. Defaults reflow with the viewport, while any
  // icon the user drags is remembered in `userPositions` and wins over these.
  const defaultPositions = useMemo(() => {
    const positions: Record<string, { x: number; y: number }> = {};
    const usableH = Math.max(ICON_CELL_H, area.height - layout.dockHeight - 28);
    const perCol = Math.max(1, Math.floor(usableH / ICON_CELL_H));

    const primary = desktopApps.filter((a) => !a.easterEgg);
    const eggs = desktopApps.filter((a) => a.easterEgg);

    primary.forEach((app, i) => {
      const col = Math.floor(i / perCol);
      const row = i % perCol;
      positions[app.id] = { x: col * (ICON_W + 6), y: row * ICON_CELL_H };
    });

    const eggX = Math.max(0, area.width - ICON_W - 14);
    eggs.forEach((app, j) => {
      const y =
        area.height - layout.dockHeight - 20 - (eggs.length - j) * ICON_CELL_H;
      positions[app.id] = { x: eggX, y: Math.max(0, y) };
    });

    return positions;
  }, [area.width, area.height, desktopApps]);

  const iconRect = (id: string) => {
    const pos = userPositions[id] ?? defaultPositions[id] ?? { x: 0, y: 0 };
    const left = pos.x;
    const top = layout.menuBarHeight + pos.y;
    return { left, top, right: left + ICON_W, bottom: top + ICON_BOX_H };
  };

  // Start a rubber-band selection only when the press lands on empty desktop
  // (the click passes through the pointer-events:none layers to the Root).
  const handleDesktopMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0 || e.target !== e.currentTarget) return;
    marqueeStart.current = { x: e.clientX, y: e.clientY };
    setSelectedIds([]);
    setMarquee({ x: e.clientX, y: e.clientY, w: 0, h: 0 });
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const start = marqueeStart.current;
      if (!start) return;
      const x = Math.min(start.x, e.clientX);
      const y = Math.min(start.y, e.clientY);
      const w = Math.abs(e.clientX - start.x);
      const h = Math.abs(e.clientY - start.y);
      setMarquee({ x, y, w, h });
      const box = { left: x, top: y, right: x + w, bottom: y + h };
      const hits = desktopApps
        .filter((app) => {
          const r = iconRect(app.id);
          return (
            r.left < box.right &&
            r.right > box.left &&
            r.top < box.bottom &&
            r.bottom > box.top
          );
        })
        .map((app) => app.id);
      setSelectedIds(hits);
    };
    const onUp = () => {
      if (!marqueeStart.current) return;
      marqueeStart.current = null;
      setMarquee(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desktopApps, userPositions, defaultPositions]);

  return (
    <Root
      onMouseDown={handleDesktopMouseDown}
      style={{
        userSelect: marquee ? "none" : undefined,
        cursor: marquee ? "crosshair" : undefined,
      }}
    >
      <Blob $color={colors.green} $size={420} style={{ top: -120, right: 120 }} />
      <Blob $color={colors.lilac} $size={360} style={{ bottom: 40, left: -80 }} />
      <Blob $color={colors.peach} $size={300} style={{ bottom: 120, right: -60 }} />

      <MenuBar />

      <IconsLayer>
        {desktopApps.map((app) => (
          <Rnd
            key={app.id}
            size={{ width: ICON_W, height: ICON_BOX_H }}
            position={
              userPositions[app.id] ??
              defaultPositions[app.id] ?? { x: 0, y: 0 }
            }
            bounds="parent"
            enableResizing={false}
            onDragStart={(_e, d) => {
              dragStart.current = { x: d.x, y: d.y };
              setDraggingId(app.id);
              // If the grabbed icon is part of a multi-selection, drag the whole
              // group together; otherwise a drag clears the selection.
              if (selectedIds.length > 1 && selectedIds.includes(app.id)) {
                const snap: Record<string, { x: number; y: number }> = {};
                selectedIds.forEach((id) => {
                  snap[id] = userPositions[id] ??
                    defaultPositions[id] ?? { x: 0, y: 0 };
                });
                groupStart.current = snap;
              } else {
                groupStart.current = null;
                setSelectedIds([]);
              }
            }}
            onDrag={(_e, d) => {
              const snap = groupStart.current;
              if (!snap) return;
              const dx = d.x - dragStart.current.x;
              const dy = d.y - dragStart.current.y;
              setUserPositions((p) => {
                const next = { ...p };
                Object.keys(snap).forEach((id) => {
                  if (id === app.id) return;
                  next[id] = {
                    x: Math.max(0, snap[id].x + dx),
                    y: Math.max(0, snap[id].y + dy),
                  };
                });
                return next;
              });
            }}
            onDragStop={(_e, d) => {
              setDraggingId(null);
              const dist = Math.hypot(
                d.x - dragStart.current.x,
                d.y - dragStart.current.y
              );
              const snap = groupStart.current;
              groupStart.current = null;
              if (dist <= CLICK_THRESHOLD) {
                openApp(app.id);
                return;
              }
              if (snap) {
                const dx = d.x - dragStart.current.x;
                const dy = d.y - dragStart.current.y;
                setUserPositions((p) => {
                  const next = { ...p };
                  Object.keys(snap).forEach((id) => {
                    next[id] =
                      id === app.id
                        ? { x: d.x, y: d.y }
                        : {
                            x: Math.max(0, snap[id].x + dx),
                            y: Math.max(0, snap[id].y + dy),
                          };
                  });
                  return next;
                });
              } else {
                setUserPositions((p) => ({
                  ...p,
                  [app.id]: { x: d.x, y: d.y },
                }));
              }
            }}
            style={{
              pointerEvents: "auto",
              zIndex: draggingId === app.id ? 5 : 1,
            }}
          >
            <DesktopIcon
              app={app}
              dragging={draggingId === app.id}
              selected={selectedIds.includes(app.id)}
            />
          </Rnd>
        ))}
      </IconsLayer>

      {marquee && (
        <Marquee
          style={{
            left: marquee.x,
            top: marquee.y,
            width: marquee.w,
            height: marquee.h,
          }}
        />
      )}

      <WindowsLayer ref={layerRef}>
        {windows.map((win) => {
          const app = getApp(win.appId);
          if (!app) return null;
          return <Window key={win.id} win={win} app={app} area={area} />;
        })}
      </WindowsLayer>

      <Dock />
    </Root>
  );
};

export default Desktop;
